import { useState, useEffect, useMemo } from "react";
import { Spinner } from "@heroui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import useUser from "@/hooks/useUser";
import useUserProfile from "@/hooks/useUserProfile";
import useOrders from "@/hooks/useOrders";
import useEntities from "@/hooks/useEntities";
import useCompanySubscription from "@/hooks/useCompanySubscription";
import useOrdersFilters from "@/hooks/useOrdersFilters";
import useOrdersGrouping from "@/hooks/useOrdersGrouping";
import { useUseCases } from "@/context/UseCasesProvider";
import { isProduction } from "@/utils/environment";
import { useDataOptions } from "./dataOptions";
import { transformOrders } from "@/utils/orderTransformers";
import OrdersHeader from "./OrdersHeader";
import OrdersFilters from "./OrdersFilters";
import OrdersTable from "./OrdersTable";
import OrdersPagination from "./OrdersPagination";
import type { Order, ApiOrder, ColumnDef, StatusOption } from "./types";

const PER_PAGE = 1000;

export default function Orders() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();

  // Hooks
  useCompanySubscription();
  const { entities } = useEntities();
  const { isDataReadOnlyOperators, isDataEntrySupervisor, isAdmin } = useUserProfile();
  const { columns: rawColumns, statusOptions: rawStatusOptions } = useDataOptions();

  // Settings
  const columns = rawColumns as ColumnDef[];
  const statusOptions = rawStatusOptions as StatusOption[];
  const settingsInterface = useUseCases();
  const { data: userData } = useUser();
  const isSettingsLoaded = settingsInterface?.settings?.display !== undefined;
  const configuredDays = isProduction
    ? (settingsInterface?.settings?.display?.control_tower?.period_filter_default ?? 3)
    : 90;

  // State
  const [orders, setOrders] = useState<Order[]>([]);
  const [lastUpdated, setLastUpdated] = useState("");
  const [refetchOrders, setRefetchOrders] = useState(false);
  const [unreadOrders, setUnreadOrders] = useState<string[]>(() => {
    return JSON.parse(localStorage.getItem("unreadOrders") || "[]");
  });

  const currentUserUUID = userData?.user_id;

  // Filters hook
  const filters = useOrdersFilters({
    orders,
    statusOptions,
    configuredDays,
    isSettingsLoaded,
  });

  // Fetch orders
  const handleUpdateLastUpdated = (date: string) => {
    setLastUpdated(date);
  };

  const { allOrders, error, isLoading } = useOrders(
    filters.periodFilterInterval,
    handleUpdateLastUpdated,
    refetchOrders,
    PER_PAGE
  );

  // Grouping hook
  const { paginatedGroups, pages, expandedGroups, toggleGroup } = useOrdersGrouping({
    filteredItems: filters.filteredItems,
    sortDescriptor: filters.sortDescriptor,
    rowsPerPage: filters.rowsPerPage,
    page: filters.page,
  });

  // Effects
  useEffect(() => {
    setRefetchOrders(true);
  }, [filters.periodFilterInterval]);

  useEffect(() => {
    if (refetchOrders) {
      setRefetchOrders(false);
    }
  }, [refetchOrders]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get("refresh") === "true") {
      setRefetchOrders(true);
    }
  }, [location, navigate]);

  // Prefetch users
  const { data: usersData } = useQuery({
    queryKey: ["AllUsers"],
    queryFn: async () => {
      const response = await axiosPrivate.get(`/users`);
      return response.data;
    },
    staleTime: 6000000,
    gcTime: 6000000,
  });

  // Build assignment list from users data
  const assignmentList = useMemo(() => {
    if (!usersData || !Array.isArray(usersData.data)) return [];
    const userGroups = ["Admins", "DataEntryOperators", "DataEntrySupervisors"];
    const entityFeatureEnabled = settingsInterface?.settings?.display?.features?.entity;

    return usersData.data
      .filter((user: any) => {
        const hasCorrectGroup = userGroups.some(group => user.user_group?.includes(group) ?? false);
        if (!hasCorrectGroup) return false;
        if (entityFeatureEnabled && userData?.entities) {
          const userEntities = (user.entities ?? '').split(',').map((id: string) => id.trim()).filter((id: string) => id !== '');
          const currentUserEntities = new Set((userData.entities ?? '').split(',').map((id: string) => id.trim()).filter((id: string) => id !== ''));
          return userEntities.some((id: string) => currentUserEntities.has(id));
        }
        return true;
      })
      .map((user: any) => ({
        uid: user.user_id,
        name: user.user_given_name + " " + user.user_name,
      }));
  }, [usersData, settingsInterface?.settings?.display?.features?.entity, userData?.entities]);

  // Transform orders when data changes
  useEffect(() => {
    if (allOrders && allOrders.length > 0) {
      const transformedOrders = transformOrders({
        allOrders: allOrders as ApiOrder[],
        currentUserUUID,
        unreadOrders,
      });
      setOrders(transformedOrders);
    }
  }, [allOrders, currentUserUUID, unreadOrders]);

  // Header columns
  const headerColumns = useMemo(() => {
    if (filters.visibleColumns === "all") return columns;
    return columns.filter((column) =>
      column.alwaysVisible || Array.from(filters.visibleColumns).includes(column.uid)
    );
  }, [filters.visibleColumns, columns]);

  // Loading and error states
  if (isLoading || !filters.periodFilterInterval || !allOrders) {
    return (
      <>
        <OrdersHeader lastUpdated={lastUpdated} />
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] gap-4">
          <Spinner size="lg" color="success" classNames={{ wrapper: "w-24 h-24", circle1: "border-b-vistagreen border-8", circle2: "border-b-vistagreen border-8" }} />
          <span className="text-vistagrey text-sm">{t("loading")}</span>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <OrdersHeader lastUpdated={lastUpdated} />
        <div>{t("error_message", { message: (error as Error).message })}</div>
      </>
    );
  }

  return (
    <>
      <OrdersHeader lastUpdated={lastUpdated} />

      <div className="flex flex-col">
        <OrdersFilters
          // Search
          filterValue={filters.filterValue}
          onSearchChange={filters.onSearchChange}
          onClearSearch={() => filters.setFilterValue("")}
          // Client filter
          clientList={filters.clientList}
          clientListFilter={filters.clientListFilter}
          onClientListFilterChange={filters.setClientListFilter}
          // Status filter
          statusOptions={statusOptions}
          statusFilter={filters.statusFilter}
          onStatusFilterChange={filters.setStatusFilter}
          // Entity filter
          showEntityFilter={settingsInterface?.settings?.display?.features?.entity}
          entityList={filters.entityList}
          entityListFilter={filters.entityListFilter}
          onEntityListFilterChange={filters.setEntityListFilter}
          // Assignment filter
          showAssignmentFilter={!!(isDataEntrySupervisor || isAdmin)}
          assignmentList={assignmentList}
          assignmentFilter={filters.assignmentFilter}
          onAssignmentFilterChange={filters.setAssignmentFilter}
          // Date filter
          periodFilterInterval={filters.periodFilterInterval}
          onPeriodFilterChange={filters.setPeriodFilterInterval}
          onRadioChange={filters.handleRadioChange}
          // Columns
          columns={columns}
          visibleColumns={filters.visibleColumns}
          onVisibleColumnsChange={filters.setVisibleColumns}
          // Pagination info
          totalOrders={orders.length}
          rowsPerPage={filters.rowsPerPage}
          onRowsPerPageChange={filters.onRowsPerPageChange}
        />

        <OrdersTable
          paginatedGroups={paginatedGroups}
          headerColumns={headerColumns}
          expandedGroups={expandedGroups}
          toggleGroup={toggleGroup}
          entities={entities || []}
          isDataReadOnlyOperators={isDataReadOnlyOperators ?? false}
          setUnreadOrders={setUnreadOrders}
          sortDescriptor={filters.sortDescriptor}
          onSortChange={filters.setSortDescriptor}
        />

        <OrdersPagination
          page={filters.page}
          pages={pages}
          onPageChange={filters.setPage}
        />
      </div>
    </>
  );
}
